import React from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Badge,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import {
  acme,
  sidebarPaperSx,
  sidebarItemSx,
  sidebarIconSx,
  sidebarSubmenuSx,
} from "../../theme/acmeTheme";
import { mergeSx } from "./utils/mergeSx";

export const SIDEBAR_WIDTH = 280;

export function Sidebar({ width = SIDEBAR_WIDTH, children, sx }) {
  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width,
        flexShrink: 0,
        "& .MuiDrawer-paper": mergeSx(
          {
            width,
            display: "flex",
            flexDirection: "column",
            top: 0,
            left: 0,
            position: "fixed",
          },
          sidebarPaperSx,
          sx,
        ),
      }}
    >
      {children}
    </Drawer>
  );
}

export function SidebarSpacer() {
  return <Box sx={{ height: "64px" }} />;
}

export function SidebarLogo({
  src = "/images/logo.svg",
  alt = "Mailsball Logo",
  to = "/",
}) {
  return (
    <Box
      sx={{
        mx: -1.5,
        mb: 2,
        py: 2.25,
        px: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: acme.colors.foreground,
        borderRadius: `${acme.radius.lg} ${acme.radius.lg} 0 0`,
        flexShrink: 0,
      }}
    >
      <Link to={to} style={{ display: "flex", alignItems: "center" }}>
        <img src={src} alt={alt} style={{ height: 18 }} />
      </Link>
    </Box>
  );
}

export function SidebarProfileCard({ children }) {
  return (
    <Box
      sx={{
        mb: 2,
        p: 1,
        borderRadius: acme.radius.md,

        backgroundColor: acme.colors.background,
      }}
    >
      {children}
    </Box>
  );
}

export function SidebarNav({ children }) {
  return (
    <Box sx={{ overflow: "auto", flex: 1 }}>
      <List component="nav" sx={{ pl: 0, pr: 0 }}>
        {children}
      </List>
    </Box>
  );
}

export function SidebarSection({ children, borderTop = true }) {
  return (
    <Box
      sx={{
        mt: "auto",
        ...(borderTop
          ? { borderTop: `1px solid ${acme.colors.border}`, pt: 1 }
          : {}),
      }}
    >
      <List component="nav" sx={{ pl: 0, pr: 0, py: 0 }}>
        {children}
      </List>
    </Box>
  );
}

export function SidebarItem({ to, icon: Icon, label, active, sub, badges }) {
  return (
    <ListItemButton
      component={Link}
      to={to}
      sx={sidebarItemSx(active, { sub })}
    >
      <ListItemIcon sx={sidebarIconSx(active)}>
        <Icon />
      </ListItemIcon>
      <ListItemText primary={label} />
      {badges}
    </ListItemButton>
  );
}

export function SidebarGroup({
  label,
  icon: Icon,
  open,
  onToggle,
  active,
  children,
}) {
  return (
    <>
      <ListItemButton
        onClick={onToggle}
        sx={mergeSx(sidebarItemSx(false, { open }), { position: "relative" })}
      >
        <ListItemIcon sx={sidebarIconSx(active, open)}>
          <Icon />
        </ListItemIcon>
        <ListItemText primary={label} />
        <Box sx={{ position: "absolute", right: 8 }}>
          {open ? (
            <ExpandLess
              sx={{
                color: acme.colors.mutedForegroundSolid,
                fontSize: "1.25rem",
              }}
            />
          ) : (
            <ExpandMore
              sx={{
                color: acme.colors.mutedForegroundSolid,
                fontSize: "1.25rem",
              }}
            />
          )}
        </Box>
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit sx={sidebarSubmenuSx}>
        <List component="div" disablePadding>
          {children}
        </List>
      </Collapse>
    </>
  );
}

export function SidebarBadge({ content, tone = "primary" }) {
  const bg =
    tone === "secondary"
      ? acme.colors.mutedForegroundSolid
      : acme.colors.foreground;
  return (
    <Badge
      badgeContent={content}
      sx={{
        "& .MuiBadge-badge": {
          backgroundColor: bg,
          color: acme.colors.background,
          borderRadius: acme.radius.sm,
          minWidth: "20px",
          height: "20px",
          fontSize: "0.7rem",
        },
      }}
    />
  );
}

export function SidebarFooter({ children }) {
  return (
    <Box
      sx={{
        mt: 1,
        mb: 2,
        position: "sticky",
        bottom: 0,
        width: "100%",
        backgroundColor: acme.colors.background,
        display: "flex",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {children}
    </Box>
  );
}
